import { useCallback } from "react";
import { useAtom, useSetAtom } from "jotai";
import { type AuthUser } from "~/api/auth/AuthUser";
import {
  featureFlagsAtom,
  featureFlagStore,
  unpersistedFeatureFlagsAtom,
} from "~/atoms/featureFlagsAtoms";
import {
  getFeatureFlagDefaults,
  type FeatureFlagLocalKey,
} from "~/feature-flags-defaults";
import {
  applyAllOverrideFeatureFlags,
  FEATURE_FLAGS_STORAGE_KEY,
} from "~/utils/feature-flags-utils";

export const useFeatureFlags = () => {
  const [flags, setFlags] = useAtom(featureFlagsAtom, {
    store: featureFlagStore,
  });
  const setUnpersistedFlags = useSetAtom(unpersistedFeatureFlagsAtom, {
    store: featureFlagStore,
  });

  const setUserFlags = useCallback(
    (user: AuthUser, resetStorage: boolean) => {
      const resolved = applyAllOverrideFeatureFlags(
        user,
        getFeatureFlagDefaults(),
      );

      // The impersonated user's flags are theirs, not the staff user's, so they
      // must not replace the overrides waiting for the session to stop.
      if (user?.isImpersonated) {
        setUnpersistedFlags(resolved);
        return;
      }

      if (resetStorage) localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);

      setFlags(resolved);
    },
    [setFlags, setUnpersistedFlags],
  );

  return {
    flags,
    // TODO: convert flag state to directly usable.
    isEnabled: (key: FeatureFlagLocalKey) => flags[key] ?? false,
    toggleFlag: (key: FeatureFlagLocalKey) => {
      setFlags({ [key]: !flags[key] });
    },
    updateUserFlags: (user: AuthUser) => {
      setUserFlags(user, !user);
    },
    resetFlags: (user: AuthUser) => {
      setUserFlags(user, true);
    },
  };
};

/**
 * FeatureFlag getter.
 * Synchronously read feature flags from store.
 * Used in route guards where we need immediate access before React hydration.
 */
export const getFeatureFlags = () => featureFlagStore.get(featureFlagsAtom);
